import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog, DialogContent, DialogActions, Button, Alert,
  TextField, Typography, Box, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import axios from "../../../config/axiosConfig";
import { Encabezado, Seccion, Rejilla, fieldSx, contenidoSx, pieSx, botonPrincipalSx } from "./dialogoUi";

const anioActual = () => new Date().getFullYear();
const initialForm = { year: String(anioActual()), cantidad: "", observacion: "" };

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1950) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const RegistrarDisponibilidadDialog = ({ open, onClose, empleado }) => {
  const [saldo, setSaldo] = useState(null);
  const [antiguedad, setAntiguedad] = useState(null);
  const [ultimoHistorial, setUltimoHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(() => {
    if (!empleado?.id) return;
    setLoading(true);
    setError("");
    axios.get(`/api/permisos/empleado/${empleado.id}/disponibilidad`)
      .then(({ data }) => {
        setSaldo(data?.data?.saldo ?? 0);
        setAntiguedad(data?.data?.antiguedad || null);
        setUltimoHistorial(data?.data?.ultimoHistorial || []);
      })
      .catch((e) => setError(e?.response?.data?.message || "Error al cargar la información."))
      .finally(() => setLoading(false));
  }, [empleado?.id]);

  useEffect(() => {
    if (open) {
      setForm(initialForm);
      cargar();
    }
  }, [open, cargar]);

  const handleChange = (campo) => (e) => setForm((prev) => ({ ...prev, [campo]: e.target.value }));

  const puedeGuardar = Number(form.year) > 0 && form.cantidad !== "" && !saving;

  const handleGuardar = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.post(`/api/permisos/empleado/${empleado.id}/registrar-historial`, {
        year: Number(form.year),
        cantidad: Number(form.cantidad),
        observacion: form.observacion.trim(),
      });
      setForm(initialForm);
      cargar();
    } catch (e) {
      setError(e?.response?.data?.message || "Error al registrar el historial.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth scroll="paper">
      <Encabezado titulo="Disponibilidad de vacaciones" subtitulo={`${empleado?.nombre || ""}${empleado?.cargo ? ` — ${empleado.cargo}` : ""}`} />
      <DialogContent sx={contenidoSx}>
        {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={22} /></Box>
        ) : (
          <>
            <Alert severity="info" sx={{ py: 0 }}>
              Días pendientes: <strong>{saldo}</strong>
              {antiguedad && <> — Antigüedad: <strong>{antiguedad.anios} año(s) {antiguedad.meses} mes(es)</strong></>}
            </Alert>

            <Seccion titulo="Historial" sinCuerpo>
              {ultimoHistorial.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", p: 1.25 }}>No tiene historial de disponibilidad.</Typography>
              ) : (
                <Table size="small" sx={{ "& td, & th": { py: 0.3, px: 0.75, fontSize: "0.74rem" }, "& th": { fontWeight: 700, color: "text.secondary" } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha de registro</TableCell>
                      <TableCell align="center">Gestión</TableCell>
                      <TableCell align="center">Cantidad</TableCell>
                      <TableCell>Observación</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ultimoHistorial.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>{formatFecha(r.fecha)}</TableCell>
                        <TableCell align="center">{r.year}</TableCell>
                        <TableCell align="center">{r.cantidad}</TableCell>
                        <TableCell>{r.observacion || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Seccion>

            <Seccion titulo="Registrar vacación por gestión">
              <Rejilla cols={2}>
                <TextField size="small" type="number" label="Año o gestión" value={form.year} onChange={handleChange("year")} sx={fieldSx} />
                <TextField size="small" type="number" label="Cantidad de días" value={form.cantidad} onChange={handleChange("cantidad")} sx={fieldSx} />
              </Rejilla>
              <TextField size="small" label="Observación" multiline minRows={2} fullWidth value={form.observacion} onChange={handleChange("observacion")} sx={{ ...fieldSx, mt: 1 }} />
            </Seccion>
          </>
        )}
      </DialogContent>
      <DialogActions sx={pieSx}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cerrar</Button>
        <Button variant="contained" onClick={handleGuardar} disabled={!puedeGuardar || loading} sx={botonPrincipalSx}>
          {saving ? <CircularProgress size={20} color="inherit" /> : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegistrarDisponibilidadDialog;
